import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDesktop,
  faUser,
  faHistory,
  faClock,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { useMonitorData } from '../hooks/useMonitorData';
import { useAuth } from '../hooks/useAuth';
import { useRoleSelection } from '../hooks/useRoleSelection';
import Button from '../components/Button';

const AtendimentosMonitor: React.FC = () => {
  const { currentOrganization } = useAuth();
  const { clearRoleSelection } = useRoleSelection();
  const [currentTime, setCurrentTime] = useState(new Date());

  const organizationId = currentOrganization?.id || '';

  const { currentPatient, lastCalled, loading, error } = useMonitorData(organizationId);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Add keyboard shortcut for role reset (Esc key)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearRoleSelection();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [clearRoleSelection]);

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPatientName = (patient: any): string => {
    return patient?.paciente?.nome || 'Paciente';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-6"></div>
          <p className="text-2xl font-light">Carregando monitor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 to-red-700 flex items-center justify-center">
        <div className="text-center text-white">
          <FontAwesomeIcon icon={faDesktop} className="w-16 h-16 mb-6 opacity-50" />
          <h1 className="text-3xl font-bold mb-4">Erro no Monitor</h1>
          <p className="text-xl font-light mb-4">{error}</p>
          <Button
            variant="outline"
            size="sm"
            leftIcon={faArrowLeft}
            onClick={clearRoleSelection}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-xs px-2 py-1"
          >
            Trocar papel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 text-white overflow-hidden">
      <div className="container mx-auto px-6 py-8 h-screen flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-12 relative">
          <div className="flex items-center space-x-4">
            <FontAwesomeIcon icon={faDesktop} className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Monitor de Atendimento</h1>
          </div>
          <div className="text-right">
            <div className="text-3xl font-mono font-bold">{formatTime(currentTime)}</div>
            <div className="text-lg font-light opacity-90">{formatDate(currentTime)}</div>
          </div>
        </div>

        {/* Current Patient */}
        <div className="flex-1 flex flex-col justify-center">
          {currentPatient ? (
            <div className="text-center mb-16">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-16 border border-white/20 animate-pulse">
                <FontAwesomeIcon icon={faUser} className="w-24 h-24 mb-8 text-yellow-300" />
                <h2 className="text-2xl font-light mb-4 text-yellow-300">CHAMANDO</h2>
                <h1 className="text-7xl font-bold mb-8">{getPatientName(currentPatient)}</h1>
                <div className="flex items-center justify-center text-2xl text-white/80">
                  <FontAwesomeIcon icon={faClock} className="mr-3" />
                  Chamado às{' '}
                  {new Date(currentPatient.called_at!).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center mb-16">
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-16 border border-white/10">
                <FontAwesomeIcon icon={faUser} className="w-24 h-24 mb-8 text-white/50" />
                <h1 className="text-6xl font-light text-white/70">Aguardando próximo paciente</h1>
              </div>
            </div>
          )}
        </div>

        {/* Last Called Patients */}
        {lastCalled.length > 0 && (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center mb-6">
              <FontAwesomeIcon icon={faHistory} className="w-6 h-6 mr-3 text-white/70" />
              <h3 className="text-2xl font-light text-white/70">Últimos Atendimentos</h3>
            </div>
            <div className="grid grid-cols-3 gap-8">
              {lastCalled.map((patient, index) => (
                <div key={patient.id} className="text-center">
                  <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                    <div className="text-3xl font-bold text-white/90 mb-2">
                      {getPatientName(patient)}
                    </div>
                    <div className="text-lg text-white/60">
                      Finalizado às{' '}
                      {new Date(patient.finished_at!).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-white/50 text-lg">
          <p>Sistema de Gestão Clínica • Atualização automática a cada 5 segundos</p>
          <p className="text-sm mt-1 text-white/30">Pressione ESC para trocar de papel</p>
        </div>
      </div>
    </div>
  );
};

export default AtendimentosMonitor;
